// this is our panels / categories for all of the different categories of genes that we display in gene.codes

import { ENV } from '../../config/runtimeEnv';
import {
	Panel, PanelSummary,
	PanelUser,
	PanelTrait,
	GlobalPanel,
	PanelRiskSummary
} from "../../types/geneCodes/Panel";
import { Staging } from '../../types/geneCodes/StagingTypes';
import type { PatientSex } from '../../utils/geneCodes/referenceRange';
import { http } from "../../lib/http/client";
const API_BASE_URL = ENV.VITE_LABIQ_SERVER;

export const getSamplesPanels = async (authSampleId: number): Promise<number[]> => {
	try {
		const response = await http.post(
			`${API_BASE_URL}/samplePanels`,
			{
				authSampleId: authSampleId,
			}
		);
		return response.data;
	} catch (error) {
		throw new Error(`Error creating application: ${error}`);
	}
};

export const createOrUpdateSamplesPanels = async (authSampleId: number, panelIds: number[]) => {
	try {
		const response = await http.post(
			`${API_BASE_URL}/createOrUpdatePanels`,
			{
				authSampleId: authSampleId,
				panelIds: panelIds.length > 0 ? panelIds : [],
			}
		);
		return response.data;
	} catch (error) {
		throw new Error(`Error creating application: ${error}`);
	}
};


/** Staff-only opt-in; omit it everywhere a patient could be looking. `assignable` is published ∪ hidden ∪ legacy NULL. */
export type PanelStatusFilter = "draft" | "published" | "hidden" | "archived" | "assignable" | "all";

/**
 * Fetch all panels with optional filters
 */
export const fetchPanels = async (
	search?: string,
	modality?: string,
	panel_sex?: string,
	withCounts: boolean = false,
	status?: PanelStatusFilter,
	withContent?: boolean
): Promise<{data: Panel[],
	total: number
}> => {
	const response = await http.get(`${API_BASE_URL}/panels`, {
		params: {
			search: search === "" ? undefined : search,
			modality: modality === "" ? undefined : modality,
			panel_sex: panel_sex === "" ? undefined : panel_sex,
			with_counts: withCounts,
			// Omitted, not defaulted: the server answers published-only without it, which is what
			// every patient-facing caller needs. An unknown value is a 422.
			...(status ? { status } : {}),
			// Omitted unless true: the assignment pickers must keep listing a wheel with no SNPs.
			...(withContent ? { with_content: 1 } : {})
		},
	});

	return {
		data: response.data.data,
		total: response.data.total
	};
};

/**
 * Fetch the wheels a caller may see for one sample: staff get the published catalogue, a patient only their assigned wheels
 */
export const fetchPanelsForSample = async (
	sampleId: string,
	sex?: PatientSex
): Promise<{ data: Panel[]; total: number }> => {
	const response = await http.get(`${API_BASE_URL}/panels/for-sample/${encodeURIComponent(sampleId)}`, {
		// Omitted when absent: the server 422s anything outside male|female|unknown.
		params: sex ? { sex } : {},
	});

	return {
		data: response.data.data,
		total: response.data.total
	};
};

/**
 * Fetch a single panel by ID
 */
export const fetchPanel = async (id: number, includeHidden?: boolean): Promise<Panel> => {
	const response = await http.get(`${API_BASE_URL}/panels/${id}`, {
		// Builder-only and honoured for staff alone; omitted, not false, like `status`.
		...(includeHidden ? { params: { include_hidden: 1 } } : {}),
	});

	return response.data.data;
};

/**
 * Create a new panel
 */
export const createPanel = async (panel: Partial<Panel>): Promise<Panel> => {
	const response = await http.post(
		`${API_BASE_URL}/panels`,
		panel
	);

	return response.data.data;
};

/**
 * Update an existing panel
 */
export const updatePanel = async (id: number, panel: Partial<Panel>): Promise<Panel> => {
	const response = await http.put(
		`${API_BASE_URL}/panels/${id}`,
		panel
	);

	return response.data.data;
};

/**
 * Delete a panel
 */
export const deletePanel = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/panels/${id}`);
};

/**
 * Get all traits for a panel
 */
// sort_by is omitted when unset so the server's rank ordering applies — the wheel and the sidebar must agree.
export const getPanelTraits = async (
	id: number,
	search?: string,
	sortBy?: string,
	sortOrder: string = 'asc',
	includeHidden?: boolean
): Promise<PanelTrait[]> => {
	const response = await http.get(`${API_BASE_URL}/panels/${id}/traits`, {
		params: {
			search,
			...(sortBy ? { sort_by: sortBy } : {}),
			sort_order: sortOrder,
			...(includeHidden ? { include_hidden: 1 } : {})
		},
	});

	return response.data.data;
};

/**
 * Get all SNPs for a panel
 */
export const getPanelSnps = async (
	id: number,
	page: number = 1,
	perPage: number = 25,
	sortBy: string = 'subcategory',
	sortOrder: string = 'asc',
	filters: {
		subcategory?: string;
		gene?: string;
		quality?: number;
		search?: string;
	} = {}
): Promise<{
	data: Staging[];
	total: number;
}> => {
	const response = await http.get(`${API_BASE_URL}/panels/${id}/snps`, {
		params: {
			page,
			per_page: perPage,
			sort_by: sortBy,
			sort_order: sortOrder,
			...filters
		},
	});

	return {
		data: response.data.data,
		total: response.data.total
	}
};

/**
 * Get a summary of panel data
 */
export const getPanelSummary = async (id: number): Promise<PanelSummary> => {
	const response = await http.get(`${API_BASE_URL}/panels/${id}/summary`);

	return response.data.data;
};

/**
 * Get users who have access to a panel
 */
export const getPanelUsers = async (id: number): Promise<PanelUser[]> => {
	const response = await http.get(`${API_BASE_URL}/panels/${id}/users`);

	return response.data.data;
};


/**
 * Get panels for a user's global
 */
export const getPanelsForUserGLobal = async (email: string): Promise<GlobalPanel[]> => {
	const response = await http.get(`${API_BASE_URL}/authCategories/${email}`);

	return response.data.data;
};


/**
 * update users global panel access
 */
export const updatePanelsForUserGLobal = async (email: string, panels:number[]): Promise<GlobalPanel[]> => {
	const response = await http.post(`${API_BASE_URL}/authCategories`,{
		authCategoryEmail: email,
		panelIds: panels
	});

	return response.data.data;
};

/**
 * Get the per-gene risk counts for every panel a sample carries
 */
export const getPanelRiskSummary = async (sampleId: string): Promise<PanelRiskSummary> => {
	const response = await http.get(`${API_BASE_URL}/panels/risk-summary/${sampleId}`);

	return response.data.data;
};

// The two membership verbs. `traits/{id}/add-snps` MOVES a SNP by rewriting its row; these CREATE and
// DELETE the edge, so a variant can sit in several wheels at once.
export interface AttachSnpsResult {
	message: string;
	data: Staging[];
	created_count: number;
	skipped_count: number;
	skipped: Array<{ rs: string; subcategory: string | null }>;
}

/**
 * Attach SNPs to a panel, creating one staging row per SNP
 */
export const attachPanelSnps = async (id: number, snps: Record<string, unknown>[]): Promise<AttachSnpsResult> => {
	const response = await http.post(
		`${API_BASE_URL}/panels/${id}/snps`,
		{ snps }
	);

	return response.data;
};

/**
 * Detach SNPs from a panel, deleting their staging rows
 */
export const detachPanelSnps = async (id: number, snpIds: number[]): Promise<{ message: string; deleted_count: number }> => {
	const response = await http.delete(`${API_BASE_URL}/panels/${id}/snps`, {
		data: { snp_ids: snpIds },
	});

	return response.data;
};

// The lifecycle verbs. Each is a bare POST answering with the wheel under `data` — fork's is the
// NEW draft, so a caller must follow the id it gets back rather than the one it sent.
export type PanelLifecycleVerb = "publish" | "unpublish" | "hide" | "unhide" | "archive" | "restore" | "fork";

const postLifecycle = async (id: number, verb: PanelLifecycleVerb): Promise<Panel> => {
	const response = await http.post(
		`${API_BASE_URL}/panels/${id}/${verb}`,
		{}
	);

	return response.data.data;
};

export const publishPanel = (id: number): Promise<Panel> => postLifecycle(id, "publish");

export const unpublishPanel = (id: number): Promise<Panel> => postLifecycle(id, "unpublish");

export const hidePanel = (id: number): Promise<Panel> => postLifecycle(id, "hide");

export const unhidePanel = (id: number): Promise<Panel> => postLifecycle(id, "unhide");

export const archivePanel = (id: number): Promise<Panel> => postLifecycle(id, "archive");

export const restorePanel = (id: number): Promise<Panel> => postLifecycle(id, "restore");

export const forkPanel = (id: number): Promise<Panel> => postLifecycle(id, "fork");
